import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Play,
    Eye,
    EyeOff,
    Edit,
    Trash2,
    Share2,
    Crown,
    Clock,
    AlertCircle,
    Copy,
    ExternalLink,
    ArrowUpDown,
    Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format expiration badge
const getExpiryBadge = (dateString, isExpiredStatus) => {
    if (!dateString || isExpiredStatus) return null;

    const now = new Date();
    const expiry = new Date(dateString);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return null;

    const colorClass = diffDays <= 3 ? "bg-red-500/10 text-red-500" : "bg-orange-500/10 text-orange-500";

    return (
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md ${colorClass} ml-auto`}>
            <Clock className="w-3 h-3" />
            <span className="text-xs font-medium">
                Expires in {diffDays} {diffDays === 1 ? 'day' : 'days'}
            </span>
        </div>
    );
};

const SessionCard = ({ session, onEdit, onDelete, onShare, onTogglePrivacy, isPro }) => {
    const isExpired = session.status === 'expired';
    const isHidden = session.status === 'hidden';
    const navigate = useNavigate();

    const getStatusBadge = () => {
        if (isExpired) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-destructive/20 text-destructive text-xs rounded-full">
                    <AlertCircle className="w-3 h-3" />
                    Expired
                </span>
            );
        }
        if (isHidden) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs rounded-full">
                    <AlertCircle className="w-3 h-3" />
                    Hidden
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-500 text-xs rounded-full">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                Active
            </span>
        );
    };

    return (
        <div className={`card-base overflow-hidden ${isExpired ? 'opacity-60' : ''}`}>
            <div
                className="aspect-video bg-muted relative cursor-pointer group"
                onClick={() => navigate(`/watch/${session.id}`)}
            >
                {session.clip?.thumbnail_url ? (
                    <img
                        src={session.clip.thumbnail_url}
                        alt={session.title}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-card">
                        <Play className="w-10 h-10 text-muted-foreground" />
                    </div>
                )}

                {/* Privacy Overlay Icon */}
                {session.is_private && (
                    <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm p-1.5 rounded-full z-10">
                        <EyeOff className="w-4 h-4 text-white" />
                    </div>
                )}

                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Play className="w-12 h-12 text-white" />
                </div>

                {/* Expired overlay */}
                {isExpired && (
                    <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                        <Clock className="w-8 h-8 text-destructive mb-2" />
                        <p className="text-sm font-medium">Link Expired</p>
                        {!isPro && (
                            <Button
                                size="sm"
                                className="mt-2 btn-primary"
                                onClick={(e) => { e.stopPropagation(); navigate('/pricing'); }}
                            >
                                <Crown className="w-3 h-3 mr-1" />
                                Restore
                            </Button>
                        )}
                    </div>
                )}
            </div>

            <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-heading text-sm uppercase tracking-wide truncate flex-1">
                        {session.title}
                    </h3>
                    {getStatusBadge()}
                </div>

                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" />
                            <span>{session.views} views</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{new Date(session.created_at).toLocaleDateString()}</span>
                        </div>
                    </div>

                    {getExpiryBadge(session.expires_at, isExpired)}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-4 gap-2">
                    {/* Privacy Toggle Button */}
                    <Button
                        variant={session.is_private ? "secondary" : "outline"}
                        size="sm"
                        className="w-full px-0"
                        onClick={() => onTogglePrivacy(session)}
                        disabled={isExpired || isHidden}
                        title={session.is_private ? "Make Public" : "Make Private"}
                    >
                        {session.is_private ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 text-muted-foreground" />}
                    </Button>

                    {/* Edit Button */}
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full px-0"
                        onClick={() => onEdit(session)}
                        disabled={isHidden}
                        title="Edit SubHack"
                    >
                        <Edit className="w-3.5 h-3.5" />
                    </Button>

                    {/* Share Button */}
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full px-0"
                        onClick={() => onShare(session)}
                        disabled={isExpired || isHidden}
                        title="Share Link"
                    >
                        <Share2 className="w-3.5 h-3.5" />
                    </Button>

                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full px-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => onDelete(session)}
                        title="Delete SubHack"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ... Skeleton component remains the same ...
const SessionSkeleton = () => (
    <div className="card-base overflow-hidden">
        <Skeleton className="aspect-video" />
        <div className="p-4 space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <div className="flex gap-2">
                <Skeleton className="h-8 flex-1" />
                <Skeleton className="h-8 flex-1" />
            </div>
        </div>
    </div>
);

export default function Dashboard() {
    const navigate = useNavigate();
    const { isPro, isFree } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, session: null });
    const [shareDialog, setShareDialog] = useState({ open: false, session: null });
    const [sortBy, setSortBy] = useState('newest');

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        try {
            const response = await axios.get(`${API}/my-sessions`);
            setSessions(response.data);
        } catch (error) {
            console.error('Failed to fetch sessions:', error);
            toast.error('Failed to load your projects');
        } finally {
            setLoading(false);
        }
    };

    // --- NEW: Toggle Privacy Function ---
    const handleTogglePrivacy = async (session) => {
        const newPrivacyStatus = !session.is_private;
        
        // Optimistic UI update
        setSessions(sessions.map(s => 
            s.id === session.id ? { ...s, is_private: newPrivacyStatus } : s
        ));

        try {
            await axios.patch(`${API}/session/${session.id}/privacy`, {
                is_private: newPrivacyStatus
            });
            toast.success(newPrivacyStatus ? 'Clip is now Private' : 'Clip is now Public');
        } catch (error) {
            // Revert on failure
            setSessions(sessions.map(s => 
                s.id === session.id ? { ...s, is_private: !newPrivacyStatus } : s
            ));
            toast.error('Failed to update privacy settings');
        }
    };

    const handleEdit = (session) => {
        navigate(`/editor/${session.clip_id}?session=${session.id}`);
    };

    const handleDelete = async () => {
        if (!deleteDialog.session) return;

        try {
            await axios.delete(`${API}/session/${deleteDialog.session.id}`);
            setSessions(sessions.filter(s => s.id !== deleteDialog.session.id));
            toast.success('Project deleted');
        } catch (error) {
            toast.error('Failed to delete project');
        } finally {
            setDeleteDialog({ open: false, session: null });
        }
    };

    const handleShare = (session) => {
        setShareDialog({ open: true, session });
    };

    const copyShareLink = () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const url = `${backendUrl}/api/share/${shareDialog.session.id}`;
        navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
    };

    const sortedSessions = [...sessions].sort((a, b) => {
        if (sortBy === 'views') {
            return b.views - a.views;
        }
        return new Date(b.created_at) - new Date(a.created_at);
    });

    const activeCount = sessions.filter(s => s.status === 'active').length;
    const canCreate = isPro || activeCount < 1;

    return (
        <Layout>
            <div className="min-h-screen py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                        <div>
                            <h1 className="font-brand text-4xl md:text-5xl uppercase mb-4">
                                My <span className="text-primary">Creations</span>
                            </h1>
                            <p className="text-muted-foreground">
                                {sessions.length} project{sessions.length !== 1 ? 's' : ''}
                                {isFree && ` • ${1 - activeCount} slots remaining`}
                            </p>
                        </div>

                        <div className="flex items-center gap-4">
                            {isPro && sessions.length > 1 && (
                                <Button
                                    variant="outline"
                                    onClick={() => setSortBy(sortBy === 'newest' ? 'views' : 'newest')}
                                    className="gap-2"
                                >
                                    <ArrowUpDown className="w-4 h-4" />
                                    {sortBy === 'newest' ? 'Newest' : 'Most Views'}
                                </Button>
                            )}

                            <Button
                                className="btn-primary"
                                onClick={() => navigate('/gallery')}
                                disabled={!canCreate}
                            >
                                Create New
                            </Button>
                        </div>
                    </div>

                    {isFree && activeCount >= 1 && (
                        <div className="mb-8 p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Crown className="w-5 h-5 text-primary" />
                                <p className="text-sm">
                                    Enjoy your free SubHack slot! Delete and replace your active SubHack as much as you'd like - each SubHack remains live for 7 days after creation. When you're ready to unlock unlimited, non-expiring SubHack slots, consider upgrading to Pro!
                                </p>
                            </div>
                            <Button
                                size="sm"
                                className="btn-primary shrink-0"
                                onClick={() => navigate('/pricing')}
                            >
                                Upgrade
                            </Button>
                        </div>
                    )}

                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, i) => (
                                <SessionSkeleton key={i} />
                            ))}
                        </div>
                    ) : sortedSessions.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sortedSessions.map((session) => (
                                <SessionCard
                                    key={session.id}
                                    session={session}
                                    onEdit={handleEdit}
                                    onDelete={(s) => setDeleteDialog({ open: true, session: s })}
                                    onShare={handleShare}
                                    onTogglePrivacy={handleTogglePrivacy}
                                    isPro={isPro}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-24">
                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                                <Play className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <h3 className="font-heading text-xl uppercase mb-2">No Projects Yet</h3>
                            <p className="text-muted-foreground mb-6">
                                Start creating your first viral clip
                            </p>
                            <Button
                                className="btn-primary"
                                onClick={() => navigate('/gallery')}
                            >
                                Browse Gallery
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, session: null })}>
                {/* ... existing delete dialog content ... */}
                 <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Delete Project?</DialogTitle>
                        <DialogDescription>
                            This will permanently delete "{deleteDialog.session?.title}" and its share link.
                            {isFree && ' This will free up your project slot.'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, session: null })}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Dialog */}
            <Dialog open={shareDialog.open} onOpenChange={(open) => setShareDialog({ open, session: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Share Your Creation</DialogTitle>
                        <DialogDescription>
                            Anyone with this link can view your subtitled clip.
                        </DialogDescription>
                    </DialogHeader>
                    {shareDialog.session?.is_private && (
                         <div className="bg-yellow-500/10 text-yellow-500 p-2 rounded text-xs flex items-center gap-2 mb-2">
                             <EyeOff className="w-4 h-4" />
                             This clip is private, but anyone with the link can still view it.
                         </div>
                    )}
                    <div className="grid grid-cols-[1fr_auto] gap-2 p-3 bg-muted rounded-lg w-full overflow-hidden">
                        <code className="text-sm truncate min-w-0">
                            {`${window.location.origin}/watch/${shareDialog.session?.id}`}
                        </code>
                        <Button size="sm" variant="ghost" onClick={copyShareLink} className="shrink-0">
                            <Copy className="w-4 h-4" />
                        </Button>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShareDialog({ open: false, session: null })}>
                            Close
                        </Button>
                        <Button
                            className="btn-primary"
                            onClick={() => window.open(`/watch/${shareDialog.session?.id}`, '_blank')}
                        >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}